/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useMemo, memo, useEffect, useState, startTransition } from 'react';
import isEqual from 'lodash/isEqual';
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import { Tree } from '@jira/polaris__lib-tree/src/LibTreeMain.tsx';
import type { ItemId, RenderItem, OnDragEnd } from '@jira/polaris__lib-tree/src/ItemId.types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createTree, type JpdTreeItem } from '../../../common/utils/tree/index.tsx';
import { useContainerId, useJpdNavCanManageViews } from '../../../controllers/context.tsx';
import type { NavigationContainer } from '../../../common/types.tsx';
import { ViewItem } from './viewset/view/index.tsx';
import { CreateViewMenuItem } from './create-view/index.tsx';
import { TreeItemParentContainer, TreeItemContainer, TreeItemHead } from './tree-wrapper/index.tsx';
import { ViewSet } from './viewset/index.tsx';

type ViewsProps = {
	views: NavigationView[];
	parentId?: LocalViewId;
	navigationContainer: NavigationContainer;
	dragDropEnabled?: boolean;
};

// Resolve a section's server id from its localId, used to nest template views into the section
const getSectionId = (views: NavigationView[], localId?: LocalViewId): string | undefined =>
	views.find((view) => view.localId === localId)?.id;

export const Views: React.MemoExoticComponent<
	({
		views,
		navigationContainer,
		parentId,
		dragDropEnabled,
	}: ViewsProps) => React.JSX.Element | null
> = memo(
	({
		views,
		navigationContainer,
		parentId,
		dragDropEnabled = true,
	}: ViewsProps): React.JSX.Element | null => {
		const canManageViews = useJpdNavCanManageViews();
		const containerId = useContainerId();
		const { toggleView, rankView } = useNavigationActions();
		const [renderLimit, setRenderLimit] = useState<null | number>(Math.min(20, views.length));

		const viewRenderLimit = renderLimit || views.length;

		useEffect(() => {
			// After the component is mounted, we set the renderLimit to null so it can render the remaining items
			startTransition(() => {
				setRenderLimit(null);
			});
		}, []);

		// Limit the number of views to render if viewRenderLimit is provided
		const limitedViews = useMemo(() => {
			return views.slice(0, viewRenderLimit);
		}, [views, viewRenderLimit]);

		const tree = useMemo(() => createTree(limitedViews), [limitedViews]);

		const globalViewLocalIds = useMemo(() => {
			const ids: ItemId[] = [];
			if (!fg('polaris_global_views')) return ids;
			views.forEach((view) => {
				if (view.global) {
					ids.push(view.localId);
				}
			});
			return ids;
		}, [views]);

		const handleToggle = useCallback(
			(id: ItemId) => {
				if (!tree) return;
				toggleView(id);
			},
			[tree, toggleView],
		);

		const renderItem = useCallback<RenderItem<JpdTreeItem>>(
			({ item, draggableState, onExpand, onCollapse }) => {
				const itemType = item.data?.type;
				const isDraggingPreview = draggableState === 'preview';
				return (
					<>
						{itemType === 'section' && (
							<ViewSet
								onToggle={item.isExpanded ? onCollapse : onExpand}
								isExpanded={item.isExpanded || false}
								localId={item?.id}
								navigationContainer={navigationContainer}
								isDraggingPreview={isDraggingPreview}
								isReadOnly={!canManageViews}
							/>
						)}
						{itemType === 'view' && (
							<ViewItem
								navigationContainer={navigationContainer}
								localId={item?.id}
								isDraggingPreview={isDraggingPreview}
							/>
						)}
						{itemType === 'placeholder' && canManageViews && (
							<CreateViewMenuItem
								container={item?.data.parentId}
								sectionId={
									fg('pol-21863_refresh_views')
										? getSectionId(views, item?.data.parentId)
										: undefined
								}
								navigationContainer={navigationContainer}
							/>
						)}
					</>
				);
			},
			[navigationContainer, canManageViews, views],
		);

		const onDragEnd = useCallback<OnDragEnd>(
			(source, destination, isExpandedBeforeDrag) => {
				if (!tree || !destination) {
					return;
				}

				// Moved item id
				const id = tree.items[source.parentId].children[source.index];

				// Do not allow dropping sections inside sections
				const itemType = tree.items[id]?.data?.type;
				const dstItemType = tree.items[destination.parentId]?.data?.type;
				if (itemType === 'section' && dstItemType === 'section') {
					return;
				}

				// Do not allow dropping placeholders
				if (itemType === 'placeholder' || dstItemType === 'placeholder') {
					return;
				}

				// Do not allow merging views
				if (dstItemType === 'view') {
					return;
				}

				// Do not allow dropping before or between global views — they remain static at the top
				if (fg('polaris_global_views')) {
					const isDroppedOnRootLevel = destination.parentId === tree.rootId;
					const isDroppedBeforeGlobalView = (destination.index ?? 0) < globalViewLocalIds.length;
					if (isDroppedOnRootLevel && isDroppedBeforeGlobalView) {
						return;
					}
				}

				const { parentId: destinationParentId } = destination;

				// Save view ranks.
				rankView({
					id,
					parentId: tree.items[destinationParentId].id,
					srcParentId: tree.items[source.parentId].id,
					index: destination.index ?? 0,
					isExpandedBeforeDrag,
				});
			},
			[rankView, tree, globalViewLocalIds],
		);

		const isGlobalView = useCallback(
			(item: JpdTreeItem) => globalViewLocalIds.includes(item.id),
			[globalViewLocalIds],
		);

		// Allow dragging for a specific elements and only for a project admin (except for global views)
		const isDragEnabled = useCallback(
			(item: JpdTreeItem) =>
				canManageViews &&
				item.data?.type !== 'placeholder' &&
				dragDropEnabled &&
				// Global views cannot be reordered in a project's navigation
				!(isGlobalView(item) && fg('polaris_global_views')),
			[canManageViews, dragDropEnabled, isGlobalView],
		);

		// Disable dropping for placeholders and global views (to hide the drop indicator on them)
		const isDropEnabled = useCallback(
			(item: JpdTreeItem) =>
				canManageViews &&
				item.data?.type !== 'placeholder' &&
				dragDropEnabled &&
				!(isGlobalView(item) && fg('polaris_global_views')),
			[canManageViews, dragDropEnabled, isGlobalView],
		);

		if (!tree) {
			return null;
		}

		return (
			<>
				<Tree
					tree={tree}
					itemType={`tree-item-${containerId}`}
					TreeItemContainer={TreeItemContainer}
					TreeItemHead={TreeItemHead}
					TreeItemParentContainer={TreeItemParentContainer}
					renderItem={renderItem}
					onExpand={handleToggle}
					onCollapse={handleToggle}
					onDragEnd={onDragEnd}
					isDragEnabled={isDragEnabled}
					isDropEnabled={isDropEnabled}
					indentPerLevel={0}
				/>
				{canManageViews && (
					<CreateViewMenuItem
						container={parentId}
						navigationContainer={navigationContainer}
						enableSpotlight
					/>
				)}
			</>
		);
	},
	isEqual,
);
